import React from 'react';
import { BlockRenderer } from '../marketing-v2/features/email-composer/components';
import { EmailBlocksPreview } from '../marketing-v2/features/email-composer/components/EmailHtml';
import { SmsPreview } from '../marketing/MarketingEditorParts';
import { segmentLabel } from '../marketing/campaigns/CampaignsHub';

/**
 * A draft, opened inside the conversation.
 *
 * Clicking a draft used to navigate straight to the campaigns page. That threw
 * the person out of the thread they were in the middle of — and the thread is
 * the context: what Bailey suggested, what it based it on, what they were
 * about to ask next. Losing all of that to go and look at the thing being
 * discussed is backwards.
 *
 * So the draft opens here, and going to the full editor stays a deliberate
 * second step. It also makes the assistant consistent with itself: a proposed
 * loyalty scheme already opens its designer over the chat rather than
 * navigating away.
 *
 * The previews are the REAL ones. Email blocks go through the composer's own
 * BlockRenderer and texts through the same bubble the reminder editor uses, so
 * what is shown here is what the composer shows and what the client receives.
 * A hand-rolled approximation would drift, and drift in a preview is a lie
 * about something they are about to send.
 */

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function DraftCard({ draft, loading, error, onOpenFull, onRetry, revised = false }) {
    if (loading) {
        return (
            <div className="agt-card agt-draft">
                <p className="agt-card-body agt-draft-loading">Opening that draft…</p>
            </div>
        );
    }

    if (error || !draft) {
        return (
            <div className="agt-card agt-draft">
                <h4 className="agt-card-title">That draft wouldn’t open</h4>
                {/* Never silently swallow it: the draft is listed in the rail,
                    so the person can see it exists and would rightly wonder. */}
                <p className="agt-card-body">
                    It may have been sent or deleted since. {error || ''}
                </p>
                {onRetry && (
                    <div className="agt-actions">
                        <button className="agt-btn" onClick={onRetry}>Try again</button>
                    </div>
                )}
            </div>
        );
    }

    const isCampaign = draft.kind === 'campaign';
    const isEmail = isCampaign && draft.type !== 'sms';
    const blocks = Array.isArray(draft.emailBlocks) ? draft.emailBlocks : [];
    // The campaigns page's own labeller, not a second list of names here —
    // my first attempt invented 'Lapsed clients' and 'Regulars', which are not
    // segments this product has.
    // The server's own name for it when the card came straight from the
    // tool (it names the list the draft really goes to).
    const segment = draft.audienceName || segmentLabel(draft.audienceSegment);
    const recipients = draft.cost && draft.cost.recipientCount != null
        ? draft.cost.recipientCount
        : draft.audienceSize;

    return (
        <div className="agt-card agt-draft">
            <div className="agt-draft-head">
                <h4 className="agt-card-title">{draft.title}</h4>
                <span className="agt-rail-kind">
                    {isCampaign ? (isEmail ? 'Email' : 'Text') : 'Automation'}
                </span>
            </div>
            {isCampaign && (
                // Saved already: say so, so nobody saves it a second time.
                <p className="agt-draft-status">{revised ? 'Draft updated in Campaigns' : 'Draft in Campaigns'}</p>
            )}

            {isCampaign && (
                <div className="agt-tiles">
                    {segment && (
                        <div className="agt-tile">
                            <span className="agt-tile-k">Going to</span>
                            <span className="agt-tile-v">{segment}</span>
                        </div>
                    )}
                    {recipients != null && (
                        <div className="agt-tile">
                            <span className="agt-tile-k">People</span>
                            <span className="agt-tile-v">{recipients}</span>
                        </div>
                    )}
                    {/* Cost comes from the facade that prices the real send, so
                        this figure and the approval's cannot disagree. Shown
                        only when it is actually known — a missing price must
                        never render as £0.00, which reads as free. */}
                    {draft.cost && draft.cost.totalCost != null && (
                        <div className="agt-tile">
                            <span className="agt-tile-k">Costs</span>
                            <span className="agt-tile-v">{money(draft.cost.totalCost)}</span>
                        </div>
                    )}
                </div>
            )}

            {isEmail && draft.subject && (
                <p className="agt-draft-subject">
                    <span className="agt-draft-label">Subject</span> {draft.subject}
                </p>
            )}

            {isEmail && !!blocks.length && (
                <div className="agt-draft-preview">
                    {/* Non-interactive on purpose: this is a look at the email,
                        not a place to edit it. Editing happens in the composer,
                        which is one tap away below. */}
                    <div className="agt-draft-email" aria-hidden>
                        <EmailBlocksPreview
                            blocks={blocks}
                            fallback={() => blocks.map((block, i) => (
                                <BlockRenderer key={i} block={{ ...block, id: `agt-draft-${i}` }} />
                            ))}
                        />
                    </div>
                </div>
            )}

            {/* Words only (Bailey's drafts): the words, and that the design
                comes next in the editor. */}
            {isEmail && !blocks.length && draft.message && (
                <div className="agt-draft-words">
                    <p className="agt-draft-text">{draft.message}{draft.messageTruncated ? '…' : ''}</p>
                </div>
            )}
            {isEmail && !blocks.length && draft.needsDesign && (
                <p className="agt-card-body agt-draft-foot">It needs its design before it can go: open it to pick the look.</p>
            )}

            {isCampaign && !isEmail && draft.message && (
                <div className="agt-draft-sms"><SmsPreview text={draft.message} /></div>
            )}

            {draft.kind === 'automation' && (
                <p className="agt-card-body">
                    {(draft.steps || []).length
                        ? `${draft.steps.length} step${draft.steps.length > 1 ? 's' : ''}, switched off.`
                        : 'No steps yet, switched off.'}
                </p>
            )}

            <div className="agt-actions">
                <button className="agt-btn is-primary" onClick={onOpenFull}>
                    {isCampaign ? 'Open in Campaigns' : 'Open in Journeys'}
                </button>
            </div>
            <p className="agt-card-body agt-draft-foot">
                {/* Plainly, so nobody thinks looking at it has sent it. */}
                Nothing goes out until you send it there.
            </p>
        </div>
    );
}
